import React, { useState, useEffect, useRef, useCallback } from 'react';
import Icon from '../../../components/AppIcon';
import { supabase } from '../../../lib/supabase';

const GpsTracker = ({ ticketId, isOnline, queueAction }) => {
  const [tracking, setTracking] = useState(false);
  const [currentLocation, setCurrentLocation] = useState(null);
  const [locationError, setLocationError] = useState('');
  const [locationLogs, setLocationLogs] = useState([]);
  const [checkInDone, setCheckInDone] = useState(false);
  const [saving, setSaving] = useState(false);
  const watchIdRef = useRef(null);
  const lastLogTime = useRef(null);
  const LOG_INTERVAL_MS = 60000; // log every 60 seconds

  useEffect(() => {
    return () => {
      if (watchIdRef?.current) {
        navigator?.geolocation?.clearWatch(watchIdRef?.current);
      }
    };
  }, []);

  useEffect(() => {
    if (ticketId && isOnline) {
      fetchRecentLogs();
    }
  }, [ticketId, isOnline]);

  const fetchRecentLogs = async () => {
    try {
      const { data } = await supabase?.from('technician_gps_logs')
        ?.select('*')
        ?.eq('ticket_id', ticketId)
        ?.order('recorded_at', { ascending: false })
        ?.limit(5);
      setLocationLogs(data || []);
    } catch (err) {
      // silent
    }
  };

  const logLocation = useCallback(async (position, eventType = 'tracking') => {
    const now = Date.now();
    if (eventType === 'tracking' && lastLogTime?.current && (now - lastLogTime?.current) < LOG_INTERVAL_MS) {
      return;
    }
    lastLogTime.current = now;

    const payload = {
      ticket_id: ticketId,
      latitude: position?.coords?.latitude,
      longitude: position?.coords?.longitude,
      accuracy: position?.coords?.accuracy,
      altitude: position?.coords?.altitude,
      speed: position?.coords?.speed,
      heading: position?.coords?.heading,
      event_type: eventType,
      recorded_at: new Date()?.toISOString()
    };

    try {
      if (isOnline) {
        await supabase?.from('technician_gps_logs')?.insert(payload);
        setLocationLogs(prev => [{ ...payload, id: Date.now() }, ...prev]?.slice(0, 5));
      } else {
        await queueAction?.('LOG_GPS', payload);
      }
    } catch (err) {
      console.error('GPS log error:', err?.message);
    }
  }, [ticketId, isOnline, queueAction]);

  const startTracking = () => {
    setLocationError('');
    if (!navigator?.geolocation) {
      setLocationError('Geolocation is not supported by your browser.');
      return;
    }
    setTracking(true);
    watchIdRef.current = navigator?.geolocation?.watchPosition(
      (position) => {
        setCurrentLocation(position?.coords);
        logLocation(position, 'tracking');
      },
      (err) => {
        setLocationError(err?.message || 'Unable to get location.');
        setTracking(false);
      },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 30000 }
    );
  };

  const stopTracking = () => {
    if (watchIdRef?.current) {
      navigator?.geolocation?.clearWatch(watchIdRef?.current);
      watchIdRef.current = null;
    }
    setTracking(false);
  };

  const handleCheckIn = async () => {
    setSaving(true);
    setLocationError('');
    if (!navigator?.geolocation) {
      setLocationError('Geolocation not supported.');
      setSaving(false);
      return;
    }
    navigator?.geolocation?.getCurrentPosition(
      async (position) => {
        setCurrentLocation(position?.coords);
        await logLocation(position, 'check_in');
        setCheckInDone(true);
        setSaving(false);
      },
      (err) => {
        setLocationError(err?.message || 'Could not get location for check-in.');
        setSaving(false);
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const formatCoords = (lat, lng) => {
    if (!lat || !lng) return 'Unknown';
    return `${Number(lat)?.toFixed(5)}, ${Number(lng)?.toFixed(5)}`;
  };

  const formatTime = (ts) => {
    if (!ts) return '';
    return new Date(ts)?.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
  };

  const getEventIcon = (type) => {
    if (type === 'check_in') return 'LogIn';
    if (type === 'check_out') return 'LogOut';
    return 'MapPin';
  };

  return (
    <div className="p-5 border-b border-border">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-foreground flex items-center gap-2">
          <Icon name="Navigation" size={16} color="var(--color-primary)" />
          GPS Location Tracking
        </h3>
        {tracking && (
          <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full flex items-center gap-1 animate-pulse">
            <div className="w-1.5 h-1.5 bg-blue-500 rounded-full" />
            Live
          </span>
        )}
      </div>

      {currentLocation && (
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 mb-3">
          <div className="flex items-start gap-2">
            <Icon name="MapPin" size={16} color="#2563eb" />
            <div className="flex-1 min-w-0">
              <p className="text-xs font-medium text-blue-800">Current Position</p>
              <p className="text-xs text-blue-600 font-mono mt-0.5">
                {formatCoords(currentLocation?.latitude, currentLocation?.longitude)}
              </p>
              {currentLocation?.accuracy && (
                <p className="text-xs text-blue-500 mt-0.5">
                  Accuracy: ±{Math.round(currentLocation?.accuracy)}m
                </p>
              )}
            </div>
            <a
              href={`https://maps.google.com/?q=${currentLocation?.latitude},${currentLocation?.longitude}`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs text-blue-600 hover:underline flex items-center gap-0.5"
            >
              <Icon name="ExternalLink" size={12} color="currentColor" />
              Map
            </a>
          </div>
        </div>
      )}

      {locationError && (
        <div className="flex items-center gap-2 p-2 bg-red-50 border border-red-200 rounded-lg mb-3">
          <Icon name="AlertCircle" size={14} color="#ef4444" />
          <p className="text-xs text-red-600">{locationError}</p>
        </div>
      )}

      <div className="flex gap-2 mb-3">
        <button
          onClick={handleCheckIn}
          disabled={saving || checkInDone}
          className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-colors flex items-center justify-center gap-1.5 ${
            checkInDone
              ? 'bg-green-100 text-green-700 border border-green-200' :'bg-primary/10 text-primary hover:bg-primary/20 border border-primary/20'
          } disabled:opacity-60`}
        >
          {saving ? (
            <div className="w-3.5 h-3.5 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          ) : (
            <Icon name={checkInDone ? 'CheckCircle' : 'LogIn'} size={14} color="currentColor" />
          )}
          {checkInDone ? 'Checked In' : 'GPS Check-In'}
        </button>

        {!tracking ? (
          <button
            onClick={startTracking}
            className="flex-1 py-2 px-3 bg-muted text-foreground rounded-lg text-sm font-medium hover:bg-muted/80 transition-colors flex items-center justify-center gap-1.5"
          >
            <Icon name="Play" size={14} color="currentColor" />
            Track
          </button>
        ) : (
          <button
            onClick={stopTracking}
            className="flex-1 py-2 px-3 bg-red-100 text-red-700 rounded-lg text-sm font-medium hover:bg-red-200 transition-colors flex items-center justify-center gap-1.5"
          >
            <Icon name="Square" size={14} color="currentColor" />
            Stop
          </button>
        )}
      </div>

      {locationLogs?.length > 0 && (
        <div>
          <p className="text-xs text-muted-foreground mb-2">Recent location logs:</p>
          <div className="space-y-1.5">
            {locationLogs?.map((log, idx) => (
              <div key={log?.id || idx} className="flex items-center gap-2 text-xs text-muted-foreground">
                <Icon name={getEventIcon(log?.event_type)} size={12} color="currentColor" />
                <span className="capitalize">{log?.event_type?.replace('_', ' ')}</span>
                <span className="font-mono text-xs">{formatCoords(log?.latitude, log?.longitude)}</span>
                <span className="ml-auto">{formatTime(log?.recorded_at)}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {!isOnline && (
        <p className="text-xs text-yellow-600 flex items-center gap-1 mt-2">
          <Icon name="WifiOff" size={12} color="currentColor" />
          GPS logs will sync when back online
        </p>
      )}
    </div>
  );
};

export default GpsTracker;
